import React from 'react';

import { useQuery } from '@apollo/client/react';
import { c } from 'ttag';

import { Badge } from '@freelancelabs/components';

import { ListSkeleton } from '../../../components/Skeletons';
import { DataScope } from '../../../graphql/__generated__/schema';
import { AccountActivitiesDocument } from '../../../graphql/__generated__/types';
import { dataScopeLabel } from '../display';
import { DraftAttachment } from './draft';

interface Props {
    attachment: DraftAttachment;
    accountName: string;
    roleName: string;
    email: string;
    refusal?: string | null;
}

/** One recap line: account, role, and, when the perimeter is restricted, the markets by name. */
const ReviewAttachment = ({ attachment, accountName, roleName, refusal, email }: Props) => {
    const restricted = attachment.dataScope === DataScope.Restricted;

    // The same list as step 2 but without search: the recap must name everything granted,
    // including what a leftover search hid on the previous screen.
    const { data, loading } = useQuery(AccountActivitiesDocument, {
        variables: { organizationId: attachment.organizationId, email, search: '' },
        skip: !restricted,
    });
    const activities = data?.accountActivities ?? null;

    // Named, never fewer lines than ids: an unresolved name prints as its opaque id.
    const byId = new Map<string, string | null>();
    for (const need of activities?.needs ?? []) {
        byId.set(need.id as string, need.name ?? null);
    }
    for (const group of activities?.marketGroups ?? []) {
        for (const service of group.services) {
            byId.set(service.id as string, service.name ?? null);
        }
    }
    const granted = [
        ...attachment.needIds.map((id) => ({ id, kind: 'need' as const })),
        ...attachment.serviceIds.map((id) => ({ id, kind: 'service' as const })),
    ];

    // Guaranteed to be for the current address, or absent: applyPatch drops a stale snapshot.
    const preTicked = new Set([
        ...(attachment.suggestion?.needIds ?? []),
        ...(attachment.suggestion?.serviceIds ?? []),
    ]);
    const matchedContactCount = attachment.suggestion?.matchedContactCount ?? 0;

    return (
        <li className="flex flex-col gap-1 border-t border-border-norm pt-2 text-sm first:border-0 first:pt-0">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <b className="text-text-900">{accountName}</b>
                <span className="text-text-600">{roleName}</span>
                <span className="text-text-600">
                    {dataScopeLabel(attachment.dataScope, attachment.needIds.length + attachment.serviceIds.length)}
                </span>
                {refusal && (
                    <span className="text-danger-600" role="alert">
                        {c('Onboarding recap').t`Role no longer grantable by you — ${refusal}`}
                    </span>
                )}
            </div>

            {/* Said again here: a shared mailbox may have pre-ticked one person's markets on another's business. */}
            {restricted && matchedContactCount > 1 && (
                <p className="text-warning-700" role="alert">
                    {c('Onboarding recap')
                        .t`This address matches ${matchedContactCount} contacts — a shared mailbox. Some of the markets below were pre-ticked from another person's business.`}
                </p>
            )}

            {restricted &&
                (loading && !activities ? (
                    <ListSkeleton rows={4} />
                ) : (
                    <ul className="flex flex-col gap-1">
                        {granted.map(({ id, kind }) => (
                            <li key={id} className="flex flex-wrap items-center gap-2 text-text-600">
                                <span>{byId.get(id) ?? id}</span>
                                {/* The type is said: a need and a service carry lookalike names. */}
                                <Badge category="default" size="small">
                                    {kind === 'need' ? c('Onboarding recap').t`Need` : c('Onboarding recap').t`Service`}
                                </Badge>
                                {/* The same wording as step 2, so the two screens don't describe one fact two ways. */}
                                {preTicked.has(id) && (
                                    <Badge category="info" size="small">
                                        {c('Onboarding recap').t`Named by the source`}
                                    </Badge>
                                )}
                            </li>
                        ))}
                    </ul>
                ))}
        </li>
    );
};

export default ReviewAttachment;
